<template>
  <Loader
    :value="promises.fetch"
    :message="$t('loading', { resource: $tc('project', 2).toLowerCase() }) + '...'"
    class="projects"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title">
        <template #actions>
          <Actions placement="projects" />
          <ButtonComponent
            v-if="$store.getters.hasAccess('admin')"
            tag="router-link"
            :to="{ name: 'project-create' }"
            :label="$t('createProject')"
            :icon="['fal', 'plus']"
            theme="primary"
          />
        </template>
      </HeaderComponent>
      <CustomProjectList v-if="customListConfig.items" v-bind="customListConfig" />
      <template v-else>
        <div class="row between bottom mb-2">
          <FilterComponent
            v-model="filter"
            :items="['search', 'checkbox:includeArchived']"
            :default="defaultFilter"
            :options="{
              search: { placeholder: [`${$t('name')}, ${$t('street')}`] },
              includeArchived: { label: $t('showArchived') },
            }"
          />
          <ButtonGroup class="projects__view-toggle">
            <ButtonComponent
              v-tooltip="$t('listView')"
              :icon="['fal', 'list']"
              @click="setViewType('list')"
              :data-active="viewType === 'list'"
            />
            <ButtonComponent
              v-tooltip="$t('gridView')"
              :icon="['fal', 'th-large']"
              @click="setViewType('grid')"
              :data-active="viewType === 'grid'"
            />
          </ButtonGroup>
        </div>

        <div v-if="items.length || states.fetch" :class="['projects__content', `projects__content--${viewType}`]">
          <template v-if="viewType === 'grid'">
            <Card v-for="item in items" :default-lang="defaultLang" :key="item.id" :value="item" />
          </template>
          <template v-else-if="viewType === 'list'">
            <ProjectsList v-bind="listOptions" />
          </template>
        </div>
        <EmptyState
          v-else
          :title="$t('noX', { x: this.$tc('project', 2).toLowerCase() })"
          :icon="['fal', 'drafting-compass']"
          action-portal
        />
      </template>
    </div>
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import HeaderComponent from '@/components/Header'
import Card from '@/components/Project/Card'
import Actions from '@/components/Actions'
import EmptyState from '@/components/EmptyState'
import ButtonGroup from '@kvass/button-group'
import FilterComponent from '@/components/Filter/Component'
import ProjectsList from '@/components/Project/List/Component'
import CustomProjectList from '@/components/CustomFields/ProjectList'

import FilterMixin from '@/components/Filter'
import { Debounce } from '@/utils'
import tracker from '@/tracking'
const storageKey = `${encodeURI(window.location.hostname)}:projects:viewType`

export default {
  mixins: [FilterMixin({ default: { search: '', includeArchived: false } })],
  data() {
    return {
      viewType: localStorage.getItem(storageKey) || 'grid',
    }
  },
  watch: {
    filter: {
      handler: 'fetch',
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...mapState('Project', ['items', 'promises', 'states']),
    ...mapState(['enums']),

    defaultLang() {
      if (!this.enums) return
      return ((this.enums.Languages || []).find(i => Boolean(i.default)) || {}).value
    },
    customListConfig() {
      return {
        ...(this.enums.ProjectList || {}),
        parentIsActive: true,
        selectable: false,
        wrappers: this.listOptions.wrappers,
      }
    },
    listOptions() {
      return {
        value: this.items,
        config: [
          {
            id: 'name',
            component: 'Name',
            label: this.$t('name'),
            size: '1fr',
          },

          {
            id: 'addon',
            component: 'Icons',
            label: this.$tc('addon', 2),
            size: '1fr',
            source: 'metadata.addons',
            iconMap: {
              'project:presentation': ['fal', 'browser'],
              'project:bid': ['fal', 'sign'],
              'project:purchase': ['fal', 'file-contract'],
              'project:shop': ['fal', 'fill-drip'],
              'project:shop-purchase': ['fal', 'shopping-cart'],
              'project:flatfinder': ['fal', 'layer-plus'],
              'project:complaint': ['fal', 'tasks'],
            },
          },

          {
            id: 'demo',
            component: 'Badge',
            label: this.$tc('tag', 2),
            size: '1fr',
            theme: 'warning',
            type: 'outlined',

            placeholder: this.$t('demo'),
          },
        ],
        wrappers: {
          row: project => ({
            component: 'router-link',
            props: {
              'data-project-id': project.id,
              to: { name: 'project-dashboard', params: { id: project.id } },
              style: { color: 'inherit' },
            },
          }),
          cell: (project, column) => ({
            component: 'div',
            props: { 'data-column-id': column.id },
          }),
        },
      }
    },
  },
  methods: {
    ...mapActions('Project', {
      fetch: Debounce(function (dispatch) {
        if (Object.values(this['filter']).filter(Boolean).length) tracker.emit(`Project:filter`, { ...this['filter'] })

        return dispatch('fetch', { filter: this.filter })
      }, 200),
    }),
    setViewType(viewType) {
      this.viewType = viewType
      localStorage.setItem(storageKey, viewType)
    },
  },
  metaInfo() {
    return {
      title: this.$tc('project', 2),
    }
  },

  components: {
    Card,
    HeaderComponent,
    Actions,
    EmptyState,
    ButtonGroup,
    FilterComponent,
    ProjectsList,
    CustomProjectList,
  },
}
</script>

<style lang="scss">
.projects {
  background-color: $background-grey;

  &__content {
    &--grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      grid-gap: $form-gap;

      & > * {
        animation: fadeIn 300ms ease;
      }
    }
  }

  &__view-toggle {
    .elder-button {
      &__icon {
        padding: 0.5em 0.75em !important;
      }

      &[data-active='true'] {
        box-shadow: inset 0 3px 6px rgba(black, 0.1);
        color: var(--primary);
      }
    }
  }
}
</style>
